import { useForm, useFieldArray } from "react-hook-form";
import { Box, Button, Separator, Text } from "@chakra-ui/react";
import { LuPlus } from "react-icons/lu";
import type {
  LaborItem,
  VehicleItem,
  CloseWorkOrderInput,
  CloseWorkOrderFormData,
  ClosedWorkOrder,
} from "../type/WorkOrder";
import { useCloseWorkOrder } from "../useCases/useCloseWorkOrder";
import { useUpdateCloseWorkOrder } from "../useCases/useUpdateCloseWorkOrder";
import { formatPrice } from "@/utils/formatPrice";
import CloseWorkOrderLaborTable from "./CloseWorkOrderLaborTable";
import CloseWorkOrderVehicleTable from "./CloseWorkOrderVehicleTable";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { toast } from "@/ui/chakra/toaster";

const createEmptyLabor = (description = ""): LaborItem => ({
  description,
  quantity: 0,
  hours: 0,
  unitPrice: 0,
  totalPrice: 0,
});

const createEmptyVehicle = (): VehicleItem => ({
  description: "",
  days: 0,
  unitPrice: 0,
  totalPrice: 0,
});

interface CloseWorkOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrderId: string;
  closedWorkOrder?: ClosedWorkOrder | null;
  onSuccess?: () => void;
}

export const CloseWorkOrderModal = ({
  isOpen,
  onClose,
  workOrderId,
  closedWorkOrder,
  onSuccess,
}: CloseWorkOrderModalProps) => {
  const isUpdate = !!closedWorkOrder;

  const { mutate: closeWorkOrder, isPending: isClosePending } =
    useCloseWorkOrder();
  const { mutate: updateCloseWorkOrder, isPending: isUpdatePending } =
    useUpdateCloseWorkOrder();

  const isPending = isClosePending || isUpdatePending;

  const getDefaultValues = (): CloseWorkOrderFormData => ({
    laborItems:
      closedWorkOrder && closedWorkOrder.labor.length > 0
        ? closedWorkOrder.labor
        : [createEmptyLabor()],
    vehicleItems:
      closedWorkOrder && closedWorkOrder.vehicles.length > 0
        ? closedWorkOrder.vehicles
        : [createEmptyVehicle()],
  });

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    reset,
    watch,
  } = useForm<CloseWorkOrderFormData>({
    values: getDefaultValues(),
  });

  const {
    fields: laborFields,
    append: appendLabor,
    remove: removeLabor,
  } = useFieldArray({ control, name: "laborItems" });

  const {
    fields: vehicleFields,
    append: appendVehicle,
    remove: removeVehicle,
  } = useFieldArray({ control, name: "vehicleItems" });

  const watchedLabor = watch("laborItems");
  const watchedVehicles = watch("vehicleItems");

  const laborTotal =
    watchedLabor?.reduce(
      (sum, item) =>
        sum +
        (Number(item.quantity) || 0) *
          (Number(item.hours) || 0) *
          (Number(item.unitPrice) || 0),
      0,
    ) ?? 0;

  const vehiclesTotal =
    watchedVehicles?.reduce(
      (sum, item) =>
        sum + (Number(item.days) || 0) * (Number(item.unitPrice) || 0),
      0,
    ) ?? 0;

  const onSubmit = (data: CloseWorkOrderFormData) => {
    if (data.laborItems.length === 0) {
      toast({
        title: "Debe agregar al menos un ítem de mano de obra.",
        status: "warning",
      });
      return;
    }

    if (data.vehicleItems.length === 0) {
      toast({
        title: "Debe agregar al menos un vehículo.",
        status: "warning",
      });
      return;
    }

    const payload: CloseWorkOrderInput = {
      labor: data.laborItems.map((item) => ({
        ...item,
        quantity: Number(item.quantity) || 0,
        hours: Number(item.hours) || 0,
        unitPrice: Number(item.unitPrice) || 0,
        totalPrice:
          (Number(item.quantity) || 0) *
          (Number(item.hours) || 0) *
          (Number(item.unitPrice) || 0),
      })),
      vehicles: data.vehicleItems.map((item) => ({
        ...item,
        days: Number(item.days) || 0,
        unitPrice: Number(item.unitPrice) || 0,
        totalPrice: (Number(item.days) || 0) * (Number(item.unitPrice) || 0),
      })),
    };

    const onMutateSuccess = () => {
      toast({
        title: isUpdate
          ? "Cierre de orden de trabajo actualizado exitosamente."
          : "Orden de trabajo cerrada exitosamente.",
        status: "success",
      });
      reset();
      onClose();
      onSuccess?.();
    };

    const onMutateError = (error: Error) => {
      toast({
        title: isUpdate
          ? "Error al actualizar el cierre de la orden de trabajo."
          : "Error al cerrar la orden de trabajo.",
        description: error.message || "Intente nuevamente.",
        status: "error",
      });
    };

    if (isUpdate) {
      updateCloseWorkOrder(
        { closedWorkOrderId: closedWorkOrder.id, data: payload },
        { onSuccess: onMutateSuccess, onError: onMutateError },
      );
    } else {
      closeWorkOrder(
        { id: workOrderId, data: payload },
        { onSuccess: onMutateSuccess, onError: onMutateError },
      );
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <DialogRoot
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size="xl"
      scrollBehavior="inside"
    >
      <DialogContent maxW="6xl">
        <DialogHeader borderBottomWidth="1px" borderColor="border">
          <DialogTitle>
            {isUpdate
              ? "Actualizar cierre de orden de trabajo"
              : "Cerrar orden de trabajo"}
          </DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />

        <DialogBody display="flex" flexDirection="column" gap={6} pt={5}>
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Text fontWeight="semibold">Mano de obra</Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => appendLabor(createEmptyLabor())}
              >
                <LuPlus />
                Agregar fila
              </Button>
            </Box>

            <CloseWorkOrderLaborTable
              control={control}
              fields={laborFields}
              remove={removeLabor}
            />

            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Text fontWeight="bold" fontSize="md">
                Total: {formatPrice(laborTotal)}
              </Text>
            </Box>
          </Box>

          <Separator />

          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Text fontWeight="semibold">Vehículos</Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => appendVehicle(createEmptyVehicle())}
              >
                <LuPlus />
                Agregar fila
              </Button>
            </Box>

            <CloseWorkOrderVehicleTable
              control={control}
              fields={vehicleFields}
              remove={removeVehicle}
            />

            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Text fontWeight="bold" fontSize="md">
                Total: {formatPrice(vehiclesTotal)}
              </Text>
            </Box>
          </Box>
        </DialogBody>

        <DialogFooter borderTopWidth="1px" borderColor="border">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            colorPalette="brand"
            onClick={rhfHandleSubmit(onSubmit)}
            loading={isPending}
          >
            {isUpdate ? "Actualizar cierre" : "Cerrar orden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
